import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AddRounded,
  SearchRounded,
  ScheduleRounded,
  InfoRounded,
  ViewAgendaRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useQueues } from '../../hooks/useQueues';
import { Queue, QueueStatus } from '../../api/queueTypes';
import { can } from '../../auth/session';
import { QueueStatusChip, ModeBadge, Rating, CapacityBar, ProviderStack } from './queueParts';

const FILTERS: { key: 'all' | QueueStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending_activation', label: 'Pending' },
  { key: 'inactive', label: 'Inactive' },
];

const cardSx = {
  bgcolor: colors.backgroundPaper,
  border: `1px solid ${colors.border}`,
  borderRadius: '14px',
  p: 2.25,
  cursor: 'pointer',
  transition: 'box-shadow .15s, transform .15s, border-color .15s',
  '&:hover': { boxShadow: '0 6px 18px rgba(15,23,42,.08)', transform: 'translateY(-2px)', borderColor: colors.primaryLight },
};

const QueuesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { listQueues } = useQueues();
  const [queues, setQueues] = useState<Queue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | QueueStatus>('all');
  const [search, setSearch] = useState('');
  const canCreate = can('can_manage_queues');

  useEffect(() => {
    let active = true;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await listQueues(1, 50);
        if (active) setQueues(res.data);
      } catch (e: any) {
        if (active) setError(e?.response?.data?.message || 'Failed to load queues.');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [listQueues]);

  const pendingCount = useMemo(() => queues.filter((q) => q.status === 'pending_activation').length, [queues]);

  const visible = useMemo(() => {
    let list = filter === 'all' ? queues : queues.filter((q) => q.status === filter);
    if (search.trim()) list = list.filter((q) => q.name.toLowerCase().includes(search.trim().toLowerCase()));
    return list;
  }, [queues, filter, search]);

  return (
    <Box>
      {/* Controls — all controls share a 40px height and are vertically centered */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              onClick={() => setFilter(f.key)}
              variant={filter === f.key ? 'contained' : 'outlined'}
              disableElevation
              sx={{
                height: 40,
                borderRadius: 99,
                px: 2.25,
                bgcolor: filter === f.key ? colors.gray900 : 'transparent',
                color: filter === f.key ? '#fff' : colors.textSecondary,
                borderColor: colors.border,
                '&:hover': { bgcolor: filter === f.key ? colors.gray900 : colors.gray50, borderColor: colors.border },
              }}
            >
              {f.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search queues"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
            sx={{ width: 230, '& .MuiOutlinedInput-root': { height: 40 } }}
          />
          {canCreate && (
            <Button variant="contained" disableElevation startIcon={<AddRounded />} onClick={() => navigate('/queues/new')} sx={{ height: 40, whiteSpace: 'nowrap' }}>
              New queue
            </Button>
          )}
        </Box>
      </Box>

      {/* Pending activation banner */}
      {pendingCount > 0 && (
        <Alert
          severity="warning"
          icon={<InfoRounded />}
          action={<Button color="inherit" size="small" onClick={() => navigate('/plans')}>Pay &amp; activate</Button>}
          sx={{ mb: 2, borderRadius: '14px' }}
        >
          <b>{pendingCount} queue{pendingCount > 1 ? 's' : ''} pending activation.</b> Complete payment to activate.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' }}>
          <Box sx={{ width: 88, height: 88, borderRadius: '24px', bgcolor: colors.primaryLight, color: colors.primary, display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
            <ViewAgendaRounded sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h3" sx={{ mb: 0.5 }}>No queues here</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            {queues.length === 0 ? 'Create your first queue to start serving customers.' : 'No queues match this filter.'}
          </Typography>
          {canCreate && queues.length === 0 && (
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => navigate('/queues/new')}>Create queue</Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3,1fr)' }, gap: 2.25 }}>
          {visible.map((q) => (
            <Box key={q.id} sx={cardSx} onClick={() => navigate(`/queues/${q.id}`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{q.name}</Typography>
                    <ModeBadge mode={q.booking_mode} />
                    <Rating value={q.average_rating} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary, mt: 0.25 }}>
                    <ScheduleRounded sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 13 }}>{q.schedule_summary || '—'}</Typography>
                  </Box>
                </Box>
                <QueueStatusChip status={q.status} />
              </Box>

              <Box sx={{ mt: 1.5 }}><ProviderStack providers={q.providers} /></Box>
              <Box sx={{ mt: 2 }}><CapacityBar queue={q} /></Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QueuesListPage;
